import { ComponentType, lazy } from "react"

const STORAGE_KEY = "chunk-reload-timestamp"
const RELOAD_COOLDOWN_MS = 10_000

const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Loading chunk",
  "ChunkLoadError",
  "Loading CSS chunk",
  "is not a valid JavaScript MIME type",
  "Importing a module script failed",
  // A stale chunk after deploy can be served as the SPA's index.html (HTTP
  // 200), so import() resolves with an object that has no `default` export.
  // React's lazy initializer then throws one of these (Chrome / Safari) while
  // reading `payload._result.default`.
  "reading 'default'",
  "_result.default",
]

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern))
}

function hasRecentReload(): boolean {
  const timestamp = sessionStorage.getItem(STORAGE_KEY)
  if (!timestamp) return false
  return Date.now() - Number(timestamp) < RELOAD_COOLDOWN_MS
}

function markReload(): void {
  sessionStorage.setItem(STORAGE_KEY, String(Date.now()))
}

function reloadForStaleChunk(): Promise<never> {
  markReload()
  window.location.reload()
  // Never resolves - the page is reloading.
  return new Promise<never>(() => {})
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const module = await importFn()
      // A stale chunk after deploy can be served as the SPA's index.html with
      // HTTP 200, so import() resolves "successfully" with an object that has
      // no `default` export. Returning it would make React's lazy initializer
      // throw "Cannot read properties of undefined (reading 'default')".
      // Treat the missing default as a stale chunk and reload once.
      if (!module || typeof module.default === "undefined") {
        if (!hasRecentReload()) return reloadForStaleChunk()
        throw new Error("Loading chunk failed: module resolved without a default export")
      }
      return module
    } catch (error) {
      if (isChunkLoadError(error) && !hasRecentReload()) {
        return reloadForStaleChunk()
      }
      throw error
    }
  })
}

export { isChunkLoadError, hasRecentReload, markReload }
